import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import StepRowCard from "../components/stepRowCard";
import StepContainer from "../stepContainer";
import JobRoleSelectionPage from "./jobSelectionPage";
import { getUserJobRole } from "../../../services/profileService";
import DetailsDialogs from "../components/detailsDialogs";
import { saveStep } from "../../../services/profileService";
import { calculateAdvancedQuery } from "../utils/calculateAdvancedQuery";
import { useSelector } from "react-redux";

const JobRoleSelection = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [isJobRoleSelection, setIsJobRoleSelection] = useState(false);
  const [selectedJobRole, setSelectedJobRole] = useState({});
  const [dialog, setDialog] = useState({ open: false });
  const childRef = useRef(); // declare a ref for child component
  const [gridInitialFilter, setGridInitialFilter] = useState(null);

  // Setting to check if the learner can change his assigned job role
  const learnerCanChangeAssignedJob = useSelector(
    (state) => state.session.tenantSettings.lxpLearnerCanChangeAssignedJob
  );

  // On step load
  useEffect(() => {
    props.setProgressValue(10);
    props.setDisableNext(true);
    // request to get user job role
    getUserJobRole().then((res) => {
      // If user doesn't have a job role, navigate to job role selection page
      if (!res.data) {
        setIsJobRoleSelection(true);
        return;
      }
      setSelectedJobRole({
        id: res.data.id,
        name: res.data.name,
        disciplineName: res.data.disciplineName,
      });
      props.setDisableNext(false);
    });
  }, []);

  useEffect(() => {
    if (selectedJobRole) {
      let filterModel = calculateAdvancedQuery([selectedJobRole]);
      setGridInitialFilter(filterModel);
    }
  }, [selectedJobRole]);

  const onStepClose = async (goStepBack = false, stepCompleted = true) => {
    let data = {
      currentStep: 0,
      isStepCompleted: stepCompleted,
      jobRoleId: selectedJobRole?.id,
    };
    // Request to save the user's selected Job Role and then navigate to next step or previous step
    await saveStep(data);
    if (stepCompleted) {
      if (goStepBack) props.navigateStepBack();
      else props.navigateStepFront();
    }
  };

  useImperativeHandle(ref, () => ({
    onStepClose: (goStepBack, stepCompleted) => {
      if (childRef.current && childRef.current.onStepClose) {
        // Call child's onStepClose, if it's rendered and has onStepClose
        return childRef.current.onStepClose(goStepBack, stepCompleted);
      }

      // Otherwise, fallback to the parent's onStepClose
      return onStepClose(goStepBack, stepCompleted);
    },
  }));

  return (
    <>
      {dialog?.open && <DetailsDialogs dialog={dialog} setDialog={setDialog} />}
      {isJobRoleSelection ? (
        <JobRoleSelectionPage
          ref={childRef}
          onBack={() => setIsJobRoleSelection(false)}
          navigateStepFront={props.navigateStepFront}
          navigateStepBack={props.navigateStepBack}
          setDisableNext={props.setDisableNext}
          initialGridState={
            gridInitialFilter
              ? { filter: { filterModel: gridInitialFilter } }
              : null
          }
        />
      ) : (
        <StepContainer
          stepTitle={t("profileScreen.jobSelectionStep.confirmation.header")}
          stepDescription={t(
            "profileScreen.jobSelectionStep.confirmation.description"
          )}
          onTransparentButtonClick={() => {
            setIsJobRoleSelection(true);
          }}
          transparentButtonLabel={t("profileScreen.btnNo")}
          onSecondaryButtonClick={onStepClose}
          secondaryButtonLabel={t("profileScreen.btnYes")}
          disableFrontButton={!selectedJobRole?.id}
          showTransparentButton={learnerCanChangeAssignedJob}
        >
          {selectedJobRole && (
            <StepRowCard
              title={t(selectedJobRole?.name)}
              description={t(selectedJobRole?.disciplineName)}
              isSelected={true}
              onInfoClicked={() =>
                setDialog({ open: true, jobId: selectedJobRole?.id })
              }
            />
          )}
        </StepContainer>
      )}
    </>
  );
});

export default JobRoleSelection;
